/**
 * ErrorBoundary — catches uncaught render errors and shows a recovery UI
 * instead of a blank screen.
 */

import { Component, type ReactNode } from "react";

interface Props {
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
}

interface State {
  readonly hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: { componentStack: string }): void {
    console.error("[ErrorBoundary] Uncaught render error", {
      error: error.message,
      stack: info.componentStack,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="w-full max-w-sm rounded-xl border border-border-primary bg-surface-primary p-8 text-center shadow-sm">
            <p className="mb-2 font-semibold text-lg text-text-primary">
              오류가 발생했습니다
            </p>
            <p className="mb-6 text-sm text-text-secondary">
              일시적인 문제가 발생했습니다. 새로고침을 시도해주세요.
            </p>
            <button
              type="button"
              className="cursor-pointer rounded-md border-none bg-accent-blue px-6 py-2 font-semibold text-sm text-white transition-colors hover:bg-accent-blue-hover"
              onClick={() => window.location.reload()}
            >
              새로고침
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
